import { useState } from 'react';
import { Plus, X, Truck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useBeschaffungswege } from '@/hooks/useBeschaffungswege';
import { heuteIso, datumKurzDe } from '@/lib/format';
import { WEG_VERLAG, KANAELE, KANAL_SONSTIGES } from '@/lib/beschaffungWege';
import { SchnellwegKnoepfe } from '@/components/ui/SchnellwegKnoepfe';
import type { BeschaffungInput } from '@/types/bestellung';
import { FOKUS_RING } from '@/lib/fokus';

// Der Beschaffungsweg, schon beim Erfassen (A4).
//
// Am Telefon steht er im selben Atemzug fest wie die Position: „das
// bestell ich bei Libri". Bisher musste dieser Satz nach dem Speichern
// in der Detailansicht nachgetragen werden — ein zweiter Gang für eine
// Angabe, die längst dastand, und in der Praxis der Gang, der liegen
// bleibt.
//
// Bewusst NICHT hier: die Lieferantenmeldung. Was der Lieferant meldet,
// kann es vor der Bestellung nicht geben; sie gehört an den fertigen
// Journaleintrag (E3/E15) und wird dort nachgetragen.
//
// Gerechnet wird auch hier nichts, was der Server rechnet — die
// Restmenge ergibt sich aus der Menge der Position minus dem, was in
// DIESEM Formular schon vergeben ist. Sobald gespeichert ist, führt das
// Journal die Summe, und die Detailansicht liest sie von dort.

type Props = {
  /** Menge der Position — mehr als sie darf nicht vergeben werden. */
  menge: number;
  /** Verlag der Position als Vorschlag beim Weg „Verlag". */
  verlagVorschlag: string | null;
  eintraege: BeschaffungInput[];
  onChange: (eintraege: BeschaffungInput[]) => void;
};

export function BeschaffungErfassen({ menge, verlagVorschlag, eintraege, onChange }: Props) {
  const [offen, setOffen] = useState(false);
  const wege = useBeschaffungswege();

  const vergeben = eintraege.reduce((s, b) => s + b.menge, 0);
  const rest = Math.max(0, menge - vergeben);
  const liste = wege.data?.items ?? [];

  function labelVon(id: number): string {
    return liste.find((w) => w.id === id)?.label ?? 'Weg';
  }

  return (
    <div className="space-y-1">
      {eintraege.length > 0 && (
        // `flex-wrap`, nicht eine Zeile: drei Chips nebeneinander laufen
        // auf 390 Pixeln über den Rand.
        <div className="flex flex-wrap items-center gap-1">
          {eintraege.map((b, i) => (
            <span
              key={`${b.beschaffungsweg_id}-${i}`}
              className="inline-flex items-center gap-1 rounded bg-cream-warm px-2 py-0.5 text-xs text-ink-soft"
            >
              <Truck aria-hidden className="h-3 w-3" />
              <span className="tabular-nums">
                {b.menge}× {b.verlag_text ?? labelVon(b.beschaffungsweg_id)}
              </span>
              <span className="text-ink-faint">· {datumKurzDe(b.bestellt_am)}</span>
              <button
                type="button"
                onClick={() => onChange(eintraege.filter((_, j) => j !== i))}
                className="touch-ziel inline-flex h-6 w-6 items-center justify-center rounded text-ink-faint hover:text-coral"
                aria-label={`${b.menge}× ${b.verlag_text ?? labelVon(b.beschaffungsweg_id)} entfernen`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* DIE SCHNELLWEGE, AUCH HIER (A19).

          Der Befund des Betreibers: „Beschaffung erfassen im Modal Neue
          Bestellung: da fehlen die Quick-Buttons Lager/Libri/Umbreit!"
          Er hatte recht — der Weg ließ sich hier erfassen, aber nur über
          das Formular, also über vier Griffe statt einem.

          Dasselbe Bauteil wie in der Detailansicht. Was ein Tipp
          bewirkt, ist hier ein anderes: dort ein Journaleintrag über den
          Server, hier ein Eintrag im Entwurf, der erst beim Speichern
          mitgeht. Deshalb meldet das Bauteil nur den Weg und die Menge.

          Sie stehen ÜBER dem Formular-Knopf, nicht darunter: der
          Normalfall gehört zuerst gelesen. */}
      <SchnellwegKnoepfe
        offen={offen ? 0 : rest}
        onWeg={(w, menge) =>
          onChange([
            ...eintraege,
            {
              beschaffungsweg_id: w.id,
              menge,
              bestellt_am: heuteIso(),
              // Auch beim Schnellweg „Verlag" bleiben beide leer (D3,
              // Entscheidung des Betreibers): Kanal und Verlagsname
              // sind freiwillig, und der Schnellweg fragt nichts.
              // Wer sie festhalten will, nimmt das Formular.
              verlag_text: null,
              kanal: null
            }
          ])
        }
      />

      {!offen && rest > 0 && (
        <button
          type="button"
          onClick={() => setOffen(true)}
          className="inline-flex min-h-[44px] items-center gap-1.5 text-xs text-ink-soft hover:text-akzent"
        >
          <Plus className="h-3 w-3" />
          {eintraege.length === 0 ? 'Beschaffung erfassen' : `Noch ${rest} offen — weiterer Weg`}
        </button>
      )}

      {offen && liste.length > 0 && (
        <Formular
          rest={rest}
          wege={liste}
          verlagVorschlag={verlagVorschlag}
          onZufuegen={(b) => {
            onChange([...eintraege, b]);
            setOffen(false);
          }}
          onAbbrechen={() => setOffen(false)}
        />
      )}
      {offen && liste.length === 0 && (
        <p className="text-xs text-ink-faint">
          {wege.isPending ? 'Beschaffungswege werden geladen …' : 'Keine Beschaffungswege gepflegt.'}
        </p>
      )}
    </div>
  );
}

type Weg = { id: number; code: string; label: string };

function Formular({
  rest,
  wege,
  verlagVorschlag,
  onZufuegen,
  onAbbrechen
}: {
  rest: number;
  wege: Weg[];
  verlagVorschlag: string | null;
  onZufuegen: (b: BeschaffungInput) => void;
  onAbbrechen: () => void;
}) {
  // Vorbelegt mit dem, was in neun von zehn Fällen stimmt: die volle
  // Restmenge, heute, der oberste Weg der Liste — dieselbe Vorbelegung
  // wie beim Nachtrag in der Detailansicht.
  const [wegId, setWegId] = useState<number | ''>('');
  const [menge, setMenge] = useState(rest);
  const [datum, setDatum] = useState(heuteIso());
  const [verlagText, setVerlagText] = useState(verlagVorschlag ?? '');
  const [kanalWahl, setKanalWahl] = useState('');
  const [kanalFrei, setKanalFrei] = useState('');

  const gewaehlt = wegId === '' ? (wege[0]?.id ?? null) : wegId;
  const istVerlag = wege.find((w) => w.id === gewaehlt)?.code === WEG_VERLAG;
  const zeigeFreitext = kanalWahl === KANAL_SONSTIGES;
  const gueltig =
    gewaehlt !== null &&
    menge >= 1 &&
    menge <= rest &&
    datum !== '' &&
    // Wer „Sonstiges" wählt, muss auch sagen was — sonst stünde am Ende
    // ein leerer Kanal, und die Auswahl hätte nichts festgehalten.
    (!istVerlag || !zeigeFreitext || kanalFrei.trim() !== '');

  function melden(): void {
    if (gewaehlt === null) return;
    const kanal = zeigeFreitext ? kanalFrei.trim() : kanalWahl;
    onZufuegen({
      beschaffungsweg_id: gewaehlt,
      menge,
      bestellt_am: datum,
      // Bei anderen Wegen gar nicht erst mitschicken. Der Server verwirft
      // sie ohnehin — aber was nicht gesendet wird, kann auch nicht
      // versehentlich ankommen.
      verlag_text: istVerlag && verlagText.trim() !== '' ? verlagText.trim() : null,
      kanal: istVerlag && kanal !== '' ? kanal : null
    });
  }

  // Klassen als Zeichenkette, nicht zusammengesetzt: der
  // Flex-Breiten-Wächter (K11) liest className aus dem Quelltext und
  // findet nur, was dort als Text steht. Eine Vorlage per Template
  // wäre für ihn unsichtbar — eine stille Lücke im Wächter.
  return (
    // `flex-wrap` mit `items-end`: dieselbe Form wie beim Nachtrag. Auf
    // 390 Pixeln steht jedes Feld in seiner eigenen Zeile, statt dass
    // vier Felder auf je 80 Pixel gequetscht werden.
    <div className="flex flex-wrap items-end gap-2 rounded-md border border-line bg-cream-warm p-2 text-xs">
      <label className="flex w-full min-w-0 flex-col gap-1 text-ink-soft sm:w-auto">
        Weg
        <select
          value={gewaehlt ?? ''}
          onChange={(e) => setWegId(Number(e.target.value))}
          className={`min-h-[44px] rounded border border-line bg-cream px-2 text-sm text-ink ${FOKUS_RING}`}
        >
          {wege.map((w) => (
            <option key={w.id} value={w.id}>
              {w.label}
            </option>
          ))}
        </select>
      </label>

      {istVerlag && (
        <label className="flex w-full min-w-0 flex-col gap-1 text-ink-soft sm:w-auto">
          Verlag
          <input
            type="text"
            value={verlagText}
            maxLength={160}
            placeholder="Bei welchem Verlag?"
            onChange={(e) => setVerlagText(e.target.value)}
            className={`min-h-[44px] rounded border border-line bg-cream px-2 text-sm text-ink ${FOKUS_RING} w-full sm:w-44`}
          />
        </label>
      )}

      {istVerlag && (
        <label className="flex w-full min-w-0 flex-col gap-1 text-ink-soft sm:w-auto">
          Kanal
          <select
            value={kanalWahl}
            onChange={(e) => setKanalWahl(e.target.value)}
            className={`min-h-[44px] rounded border border-line bg-cream px-2 text-sm text-ink ${FOKUS_RING}`}
          >
            <option value="">— wählen —</option>
            {KANAELE.map(([wert, label]) => (
              <option key={wert} value={wert}>
                {label}
              </option>
            ))}
            <option value={KANAL_SONSTIGES}>Sonstiges</option>
          </select>
        </label>
      )}

      {istVerlag && zeigeFreitext && (
        <label className="flex w-full min-w-0 flex-col gap-1 text-ink-soft sm:w-auto">
          Und zwar
          <input
            type="text"
            value={kanalFrei}
            maxLength={40}
            placeholder="Wie bestellt?"
            onChange={(e) => setKanalFrei(e.target.value)}
            className={`min-h-[44px] rounded border border-line bg-cream px-2 text-sm text-ink ${FOKUS_RING} w-full sm:w-40`}
          />
        </label>
      )}

      <label className="flex w-full min-w-0 flex-col gap-1 text-ink-soft sm:w-auto">
        Menge
        <input
          type="number"
          // Zahlentastatur auf dem Telefon: `type="number"` allein zeigt
          // dort eine Tastatur mit Sonderzeichen.
          inputMode="numeric"
          value={menge}
          min={1}
          max={rest}
          onChange={(e) => setMenge(Math.max(1, Math.min(rest, Number(e.target.value))))}
          className={`min-h-[44px] rounded border border-line bg-cream px-2 text-sm text-ink ${FOKUS_RING} w-20 tabular-nums`}
        />
      </label>

      <label className="flex w-full min-w-0 flex-col gap-1 text-ink-soft sm:w-auto">
        Bestellt am
        <input
          type="date"
          value={datum}
          onChange={(e) => setDatum(e.target.value)}
          className={`min-h-[44px] rounded border border-line bg-cream px-2 text-sm text-ink ${FOKUS_RING} w-full sm:w-40`}
        />
      </label>

      <div className="flex gap-2">
        {/* Nicht bloss „Zufügen": im Erfassungsformular steht zwei
            Zentimeter weiter oben schon ein Knopf mit genau diesem Wort
            (die Freitext-Position). Zwei gleich beschriftete Knöpfe in
            einem Formular sind für jeden mehrdeutig, der sie nicht
            geschrieben hat — und für einen Screenreader heissen sie
            schlicht zweimal dasselbe. */}
        <Button variant="secondary" onClick={melden} disabled={!gueltig}>
          Beschaffung zufügen
        </Button>
        <Button variant="ghost" onClick={onAbbrechen}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
}
